/**
 * ReplicationSerializer - serializes and deserializes actor replicated properties.
 *
 * ## Wire format
 *
 * Each actor replication payload (`Uint8Array`) is laid out as:
 * ```
 * [actor bitmask: ceil(N/8) bytes]
 * [values for each set bit in property-index order]
 * [uint8 componentCount]
 *   [uint8 subId] [component bitmask: ceil(M/8) bytes] [component values...]
 *   ...
 * ```
 *
 * Value encoding per `ReplicationNetType` (all multi-byte values are little-endian):
 * - `bool`             : 1 byte (0 or 1)
 * - `uint8/int8`       : 1 byte
 * - `uint16/int16`     : 2 bytes
 * - `int32/uint32`     : 4 bytes
 * - `float32`          : 4 bytes
 * - `float64`          : 8 bytes
 * - `string`           : uint16 byte length + UTF-8 bytes
 * - `actorRef`         : uint8 byte length (0, 8, or 16) + raw hex bytes
 * - `vector2`          : 2× float32 (x, y)
 * - `vector3/euler`    : 3× float32 (x, y, z)
 * - `vector4/quaternion`: 4× float32 (x, y, z, w)
 * - `array`            : uint16 count + N × element value
 * - `struct`           : [bitmask][values] recursively
 * - `class`            : if polymorphic: [uint32 hash][bitmask][values], else [bitmask][values]
 *
 * Nullable properties are prefixed with a 1-byte presence flag (0 = null, 1 = value follows).
 */
import 'reflect-metadata';
import { Actor } from '../actors/Actor.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { ClassRegistry } from '../systems/ClassRegistry.js';
import {
  getPropertyMetadata,
} from '../utils/serialization/decorator.js';

import {
  BinaryReader,
  BinaryWriter,
  readClassDiscriminator,
  writeClassDiscriminator,
} from './ReplicationBinaryCodec.js';

import type { NetPropertyMetadata, PropertyMetadata } from '../utils/serialization/decorator.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The key used in the `state` record to store per-component JS-value snapshots.
 * Used internally for delta comparison; NOT sent on the wire.
 */
export const REPLICATION_COMPONENTS_KEY = '$components' as const;

/**
 * Wire sentinel written instead of a uint16 element count to signal delta-array mode.
 * When the decoder reads this value it knows the array length is unchanged and a per-element
 * bitmask follows (only the changed elements are encoded). Any other uint16 value is treated
 * as a full-array count and all elements follow without a bitmask.
 * 65535 elements is impractical for replication, so this value is safe to reserve.
 */
const DELTA_ARRAY_SENTINEL = 0xFFFF as const;

/**
 * Result of {@link ReplicationSerializer.serializeActor}.
 */
export interface ActorReplicationSnapshot {
  /**
   * Complete JS-value snapshot of all replicated properties (actor + components).
   * Pass this as `lastState` on the next tick for delta comparison.
   * Keyed by property name; components are under `$components.{uuid}`.
   */
  state: Record<string, unknown>;

  /**
   * Binary-encoded wire payload ready to send.
   * Contains either the full state (when `lastState` was null) or a delta.
   * `null` when nothing changed — do not send an update packet.
   */
  payload: Uint8Array | null;
}

/**
 * Special marker for actor references in the JS-value `state` record.
 */
export interface ActorRef {
  $actorRef: string;  // UUID of the referenced actor
}

/** Check if a value is an actor reference marker. */
export function isActorRef(value: unknown): value is ActorRef {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).$actorRef === 'string'
  );
}

// ---------------------------------------------------------------------------
// Internal metadata cache
// ---------------------------------------------------------------------------

/**
 * Per-class cached replication metadata used by both encode and decode paths.
 */
interface ClassReplicationMetadata {
  /** Ordered list of replicated property names (base → derived). */
  replicatedProps: string[];
  /** propName → `onRep*` callback name. */
  repCallbacks: Map<string, string>;
  /** propName → binary net metadata. Only props with a `net` block are included. */
  netMeta: Map<string, NetPropertyMetadata>;
}

// ---------------------------------------------------------------------------
// ReplicationSerializer
// ---------------------------------------------------------------------------

/**
 * Serializes and deserializes actor replicated properties.
 *
 * - `serializeActor` produces a JS-value `state` (for next-tick delta comparison) and a
 *   binary `payload` (`Uint8Array | null`) ready to send on the wire.
 * - `deserializeIntoActor` applies a binary payload to an actor and its components.
 */
export class ReplicationSerializer {
  // Cache per class to avoid repeated reflection
  private static classMetadataCache = new Map<Function, ClassReplicationMetadata>();

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Serialize an actor's replicated properties, producing both a JS-value snapshot
   * (for future delta comparison) and a binary wire payload.
   *
   * @param actor           The actor to serialize.
   * @param lastState       The `state` snapshot from the previous call for this actor-client pair,
   *                        or `null` for the initial spawn (produces a full payload).
   * @param isOwningClient  Whether the target client is the actor's owning client.
   *                        Controls filtering of properties marked with `replicateTo: 'owner'`
   *                        or `replicateTo: 'non-owner'`.
   */
  static serializeActor(
    actor: Actor,
    lastState: Record<string, unknown> | null = null,
    isOwningClient: boolean
  ): ActorReplicationSnapshot {
    const writer = new BinaryWriter();
    const actorState: Record<string, unknown> = {};

    const lastActorState = lastState
      ? this.withoutKeys(lastState, [REPLICATION_COMPONENTS_KEY])
      : null;

    // Combined serialize+encode pass for the actor.
    const actorChanged = this.serializeObjectAndEncode(writer, actor, lastActorState, actorState, isOwningClient);

    const lastComponents = lastState
      ? ((lastState[REPLICATION_COMPONENTS_KEY] as Record<string, Record<string, unknown>>) ?? null)
      : null;

    const componentsFull: Record<string, Record<string, unknown>> = {};

    // Reserve the component-count byte; patch it once we know how many components changed.
    const componentCountOffset = writer.offset;
    writer.writeUint8(0);
    let changedComponentCount = 0;

    for (const component of actor.getComponents(SceneComponent)) {
      if (!component.isReplicated()) continue;
      const lastComp = lastComponents?.[component.uuid] ?? null;
      const compState: Record<string, unknown> = {};

      const beforeOffset = writer.offset;
      if (component.replicationSubId >= 0) writer.writeUint8(component.replicationSubId);
      const compChanged = this.serializeObjectAndEncode(writer, component, lastComp, compState, isOwningClient);

      if (compChanged && component.replicationSubId >= 0) {
        changedComponentCount++;
      } else {
        // Rollback sub-ID (if written) + bitmask + any values: nothing to send.
        writer.resetTo(beforeOffset);
      }

      componentsFull[component.uuid] = compState;
    }

    writer.patchUint8(componentCountOffset, changedComponentCount);

    const state: Record<string, unknown> = { ...actorState };
    if (Object.keys(componentsFull).length > 0) {
      state[REPLICATION_COMPONENTS_KEY] = componentsFull;
    }

    const hasChanges = actorChanged || changedComponentCount > 0;
    return { state, payload: hasChanges ? writer.getBytes() : null };
  }

  /**
   * Deserialize a binary replication payload into an actor.
   *
   * - Only properties present in the bitmask are touched.
   * - Actor references are resolved via `resolveActorRef`; unresolvable ones are returned
   *   as a Map of `dotPath → uuid` so callers can retry after more actors have spawned.
   * - `onRep*` callbacks fire depth-first (nested → parent).
   *
   * @param actor             Target actor.
   * @param payload           Binary wire payload from `serializeActor`.
   * @param componentSubIdMap Sub-ID → SceneComponent, built from `SpawnData.componentSubIds`.
   * @param resolveActorRef   Resolver for actor UUID → Actor instance.
   * @returns Map of dot-path → referenced actor UUID for any unresolved actor references.
   */
  static deserializeIntoActor(
    actor: Actor,
    payload: Uint8Array,
    componentSubIdMap?: Map<number, SceneComponent>,
    resolveActorRef?: (uuid: string) => Actor | null
  ): Map<string, string> {
    const unresolvedRefs = new Map<string, string>(); // dotPath → uuid
    const reader = new BinaryReader(payload);

    this.decodeObjectFromBinary(reader, actor, resolveActorRef, '', unresolvedRefs);

    const componentCount = reader.readUint8();
    for (let i = 0; i < componentCount; i++) {
      const subId = reader.readUint8();
      const component = componentSubIdMap?.get(subId);
      if (component) {
        this.decodeObjectFromBinary(
          reader, component, resolveActorRef,
          `${REPLICATION_COMPONENTS_KEY}.${component.uuid}`, unresolvedRefs
        );
      } else {
        throw new Error(
          `[ReplicationSerializer] Unknown component sub-ID ${subId} in replication payload. ` +
          'Packet is corrupt or componentSubIdMap is out of sync.'
        );
      }
    }

    return unresolvedRefs;
  }

  /**
   * Assign stable integer sub-IDs to all replicated components of an actor.
   * Must be called once per actor on the server before sending a spawn packet.
   *
   * @returns A `Map<subId, SceneComponent>` for the client to store on spawn.
   */
  static assignComponentSubIds(actor: Actor): Map<number, SceneComponent> {
    const map = new Map<number, SceneComponent>();
    let subId = 0;
    for (const component of actor.getComponents(SceneComponent)) {
      if (!component.isReplicated()) continue;
      component.replicationSubId = subId;
      map.set(subId, component);
      subId++;
    }
    return map;
  }

  // ---------------------------------------------------------------------------
  // Metadata
  // ---------------------------------------------------------------------------

  private static getClassMetadata(classConstructor: Function): ClassReplicationMetadata {
    let meta = this.classMetadataCache.get(classConstructor);
    if (!meta) {
      meta = this.buildClassMetadata(classConstructor);
      this.classMetadataCache.set(classConstructor, meta);
    }
    return meta;
  }

  private static buildClassMetadata(classConstructor: Function): ClassReplicationMetadata {
    const allMetadata = getPropertyMetadata(classConstructor.prototype);
    const replicatedProps: string[] = [];
    const repCallbacks = new Map<string, string>();
    const netMeta = new Map<string, NetPropertyMetadata>();

    for (const [propName, metadata] of Object.entries(allMetadata)) {
      const pm = metadata as PropertyMetadata;
      if (pm?.replicate !== true) continue;

      if (!pm.net) {
        console.warn(
          `[ReplicationSerializer] Property "${propName}" on ${classConstructor.name} has replicate:true but no net block. ` +
          'Run the property metadata generator or add a net block manually. Skipping binary encoding for this property.'
        );
        continue;
      }

      replicatedProps.push(propName);
      netMeta.set(propName, pm.net);

      const callbackName = `onRep${propName.charAt(0).toUpperCase()}${propName.slice(1)}`;
      if (typeof classConstructor.prototype[callbackName] === 'function') {
        repCallbacks.set(propName, callbackName);
      }
    }

    // Reverse so base-class properties come first (stable ordering across client/server).
    replicatedProps.reverse();

    return { replicatedProps, repCallbacks, netMeta };
  }

  private static hasReplicatedProps(ctor: Function): boolean {
    return this.getClassMetadata(ctor).replicatedProps.length > 0;
  }

  // ---------------------------------------------------------------------------
  // Combined serialize + binary encode (single pass per object)
  // ---------------------------------------------------------------------------

  /**
   * Serialize all replicated properties of `obj` into `outState` (for next-tick delta
   * comparison) and encode changed ones into `writer` in a single pass.
   *
   * The bitmask is reserved as zero bytes at the start, individual values are written
   * inline as changes are detected, and the bitmask is patched in-place once all props
   * have been processed. Unchanged struct/class values are rolled back via `resetTo`.
   *
   * @returns `true` if at least one property changed and was written.
   */
  private static serializeObjectAndEncode(
    writer: BinaryWriter,
    obj: object,
    lastState: Record<string, unknown> | null,
    outState: Record<string, unknown>,
    isOwningClient: boolean = true
  ): boolean {
    const { replicatedProps, netMeta } = this.getClassMetadata(obj.constructor);
    const forceAll = lastState === null;

    const bitmaskOffset = writer.reserveBitmask(replicatedProps.length);
    const changedIndices = new Set<number>();

    for (let i = 0; i < replicatedProps.length; i++) {
      const propName = replicatedProps[i];
      const net = netMeta.get(propName);
      if (!net) continue;

      // Skip properties whose replicateTo does not match this client's ownership status.
      const replicateTo = net.replicateTo ?? 'all';
      if (replicateTo === 'owner' && !isOwningClient) continue;
      if (replicateTo === 'non-owner' && isOwningClient) continue;

      const liveValue = (obj as Record<string, unknown>)[propName];
      const lastSer = lastState?.[propName];

      const { serialized, changed } = this.encodeValueCombined(writer, liveValue, lastSer, net, forceAll);
      outState[propName] = serialized;
      if (changed) changedIndices.add(i);
    }

    writer.patchBitmask(bitmaskOffset, replicatedProps.length, changedIndices);
    return changedIndices.size > 0;
  }

  /**
   * Encode a single property value into `writer` and return its serialized JS form
   * (for the state record) plus whether it changed.
   *
   * - Leaf types (bool, numbers, string, enum, actorRef, vectors): compare + encode in one step.
   * - Struct/class: recursive `serializeObjectAndEncode` with rollback if unchanged.
   * - Array: detect element changes via `serializeValueForState`, then encode all elements
   *   unconditionally if any changed (arrays have no per-element bitmask on the wire).
   *
   * When `forceAll` is `true` (initial spawn / null lastState), every value is treated as
   * changed and written regardless of comparison result.
   */
  private static encodeValueCombined(
    writer: BinaryWriter,
    value: unknown,
    lastSer: unknown,
    net: NetPropertyMetadata,
    forceAll: boolean
  ): { serialized: unknown; changed: boolean } {
    // Nullable null — write presence=false and short-circuit.
    if (net.nullable && (value === null || value === undefined)) {
      const changed = forceAll || lastSer !== null;
      if (changed) writer.writePresence(false);
      return { serialized: null, changed };
    }

    switch (net.netType) {
      // ---- Boolean ----
      case 'bool': {
        const v = value as boolean;
        const changed = forceAll || v !== lastSer;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          writer.writeBool(v);
        }
        return { serialized: v, changed };
      }

      // ---- Integer and float leaf types ----
      case 'uint8':
      case 'int8':
      case 'uint16':
      case 'int16':
      case 'int32':
      case 'uint32':
      case 'float32':
      case 'float64': {
        const v = typeof value === 'number' ? value : 0;
        const changed = forceAll || v !== lastSer;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          switch (net.netType) {
            case 'uint8': writer.writeUint8(v); break;
            case 'int8': writer.writeInt8(v); break;
            case 'uint16': writer.writeUint16(v); break;
            case 'int16': writer.writeInt16(v); break;
            case 'int32': writer.writeInt32(v); break;
            case 'uint32': writer.writeUint32(v); break;
            case 'float32': writer.writeFloat32(v); break;
            case 'float64': writer.writeFloat64(v); break;
          }
        }
        return { serialized: v, changed };
      }

      // ---- String ----
      case 'string': {
        const v = typeof value === 'string' ? value : '';
        const changed = forceAll || v !== lastSer;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          writer.writeString(v);
        }
        return { serialized: v, changed };
      }

      // ---- String enum (index-mapped to uint8) ----
      case 'enum': {
        const changed = forceAll || value !== lastSer;
        if (changed) {
          const opts = net.enumOptions ?? [];
          const idx = opts.indexOf(value as string);
          if (net.nullable) writer.writePresence(true);
          writer.writeUint8(idx >= 0 ? idx : 0);
        }
        return { serialized: value, changed };
      }

      // ---- Actor reference ----
      case 'actorRef': {
        const uuid = value instanceof Actor ? value.uuid : (value as ActorRef)?.$actorRef ?? '';
        const ser: ActorRef = { $actorRef: uuid };
        const prevUuid = isActorRef(lastSer) ? lastSer.$actorRef : undefined;
        const changed = forceAll || prevUuid !== uuid;
        if (changed) {
          console.log(`[TEBY] Writing reference for actor ${value instanceof Actor ? value.constructor.name : 'unknown'} (${uuid})`);
          if (net.nullable) writer.writePresence(true);
          writer.writeActorId(uuid);
        }
        return { serialized: ser, changed };
      }

      // ---- Vector2 ----
      case 'vector2': {
        const v = value as { x: number; y: number };
        const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
        const serialized = [v.x, v.y];
        const changed = forceAll || !lastArr || lastArr[0] !== v.x || lastArr[1] !== v.y;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          writer.writeVector2(v);
        }
        return { serialized, changed };
      }

      // ---- Vector3 / Euler ----
      case 'vector3':
      case 'euler': {
        const v = value as { x: number; y: number; z: number };
        const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
        const serialized = [v.x, v.y, v.z];
        const changed = forceAll || !lastArr || lastArr[0] !== v.x || lastArr[1] !== v.y || lastArr[2] !== v.z;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          if (net.netType === 'euler') writer.writeEuler(v); else writer.writeVector3(v);
        }
        return { serialized, changed };
      }

      // ---- Vector4 / Quaternion ----
      case 'vector4':
      case 'quaternion': {
        const v = value as { x: number; y: number; z: number; w: number };
        const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
        const serialized = [v.x, v.y, v.z, v.w];
        const changed = forceAll || !lastArr
          || lastArr[0] !== v.x || lastArr[1] !== v.y || lastArr[2] !== v.z || lastArr[3] !== v.w;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          writer.writeVector4(v);
        }
        return { serialized, changed };
      }

      // ---- Struct / Class ----
      case 'struct':
      case 'class': {
        if (value === null || value === undefined) {
          const changed = forceAll || lastSer !== null;
          if (changed) {
            if (net.nullable) {
              writer.writePresence(false);
            } else if (net.netType === 'class' && net.polymorphic) {
              writer.writeUint32(0); // null hash
            } else {
              // Non-nullable null struct (defensive): write empty bitmask for reader to consume.
              const meta = net.structClass ? this.getClassMetadata(net.structClass) : null;
              if (meta) writer.reserveBitmask(meta.replicatedProps.length);
            }
          }
          return { serialized: null, changed };
        }

        const lastNested = (!forceAll && lastSer !== null && lastSer !== undefined
          && typeof lastSer === 'object' && !Array.isArray(lastSer))
          ? lastSer as Record<string, unknown>
          : null;

        const nestedState: Record<string, unknown> = {};
        const beforeOffset = writer.offset;

        if (net.nullable) writer.writePresence(true);
        if (net.netType === 'class' && net.polymorphic) writeClassDiscriminator(writer, value as object);

        const nestedChanged = this.serializeObjectAndEncode(
          writer, value as object, forceAll ? null : lastNested, nestedState
        );

        if (!nestedChanged && !forceAll) {
          // Nothing inside the struct changed — roll back presence/discriminator/bitmask bytes.
          writer.resetTo(beforeOffset);
          return { serialized: nestedState, changed: false };
        }
        return { serialized: nestedState, changed: true };
      }

      // ---- Array ----
      case 'array': {
        const arr = Array.isArray(value) ? value : [];
        const lastArr = !forceAll && Array.isArray(lastSer) ? lastSer as unknown[] : null;
        const lengthChanged = lastArr === null || arr.length !== lastArr.length;
        const canDelta = !forceAll && !lengthChanged;

        const serialized: unknown[] = new Array(arr.length);

        if (canDelta) {
          // Delta mode: reserve the bitmask upfront then speculatively encode each element.
          // If unchanged, roll back to elemBefore (primitives always write; structs/classes
          // roll back internally and return changed:false, making resetTo a no-op).
          // If nothing changed at all, roll back the entire sentinel+bitmask block.
          const beforeArray = writer.offset;
          if (net.nullable) writer.writePresence(true);
          writer.writeUint16(DELTA_ARRAY_SENTINEL);
          const bitmaskOffset = writer.reserveBitmask(arr.length);
          const changedIndices = new Set<number>();

          for (let i = 0; i < arr.length; i++) {
            const elemBefore = writer.offset;
            const { serialized: elemSer, changed: elemChanged } =
              net.element
                ? this.encodeValueCombined(writer, arr[i], lastArr![i], net.element, false)
                : { serialized: arr[i], changed: false };
            serialized[i] = elemSer;
            if (elemChanged) {
              changedIndices.add(i);
            } else {
              writer.resetTo(elemBefore);
            }
          }

          if (changedIndices.size === 0) {
            writer.resetTo(beforeArray);
            return { serialized, changed: false };
          }

          writer.patchBitmask(bitmaskOffset, arr.length, changedIndices);
          return { serialized, changed: true };
        }

        // Full mode: forceAll or length changed — encode every element sequentially.
        let anyChanged = forceAll || lengthChanged;
        if (net.nullable) writer.writePresence(true);
        writer.writeUint16(arr.length);
        for (let i = 0; i < arr.length; i++) {
          const { serialized: elemSer, changed: elemChanged } =
            net.element
              ? this.encodeValueCombined(writer, arr[i], lastArr?.[i] ?? null, net.element, forceAll)
              : { serialized: arr[i], changed: false };
          serialized[i] = elemSer;
          if (elemChanged) anyChanged = true;
        }
        return { serialized, changed: anyChanged };
      }

      default:
        console.warn(`[ReplicationSerializer] Unknown netType: ${(net as NetPropertyMetadata).netType}`);
        return { serialized: value, changed: false };
    }
  }

  // ---------------------------------------------------------------------------
  // Binary decoding
  // ---------------------------------------------------------------------------

  /**
   * Decode replicated properties from `reader` into `obj`.
   * Fires `onRep*` callbacks after each property is applied.
   */
  private static decodeObjectFromBinary(
    reader: BinaryReader,
    obj: object,
    resolveActorRef: ((uuid: string) => Actor | null) | undefined,
    pathPrefix: string,
    unresolvedRefs: Map<string, string>
  ): void {
    const metadata = this.getClassMetadata(obj.constructor);
    const { replicatedProps, netMeta } = metadata;

    const present = reader.readBitmask(replicatedProps.length);

    for (let i = 0; i < replicatedProps.length; i++) {
      if (!present[i]) continue;
      const propName = replicatedProps[i];
      const net = netMeta.get(propName);
      if (!net) continue;

      const dotPath = pathPrefix ? `${pathPrefix}.${propName}` : propName;
      const currentValue = (obj as Record<string, unknown>)[propName];

      const decoded = this.decodeValueFromBinary(
        reader, currentValue, net, resolveActorRef, dotPath, unresolvedRefs
      );

      if (decoded !== UNRESOLVED_SENTINEL) {
        (obj as Record<string, unknown>)[propName] = decoded;
        this.fireRepCallback(obj, metadata, propName);
      }
    }
  }

  /**
   * Decode a single value from `reader` according to `net`.
   * Returns {@link UNRESOLVED_SENTINEL} for dangling actor references (caller must handle).
   */
  private static decodeValueFromBinary(
    reader: BinaryReader,
    currentValue: unknown,
    net: NetPropertyMetadata,
    resolveActorRef: ((uuid: string) => Actor | null) | undefined,
    dotPath: string,
    unresolvedRefs: Map<string, string>
  ): unknown {
    // Nullable presence check.
    if (net.nullable) {
      if (!reader.readPresence()) return null;
    }

    switch (net.netType) {
      case 'bool': return reader.readBool();
      case 'uint8': return reader.readUint8();
      case 'int8': return reader.readInt8();
      case 'uint16': return reader.readUint16();
      case 'int16': return reader.readInt16();
      case 'int32': return reader.readInt32();
      case 'uint32': return reader.readUint32();
      case 'float32': return reader.readFloat32();
      case 'float64': return reader.readFloat64();
      case 'string': return reader.readString();
      case 'enum': {
        const idx = reader.readUint8();
        const opts = net.enumOptions ?? [];
        return opts[idx] ?? opts[0] ?? '';
      }

      case 'actorRef': {
        const uuid = reader.readActorId();
        if (!uuid) return null; // byte length 0 = null reference
        if (resolveActorRef) {
          const resolved = resolveActorRef(uuid);
          if (resolved) {
            console.log(`[TEBY] Resolved actor reference: ${dotPath} -> ${uuid}`);
            return resolved;
          }
        }
        console.log(`[TEBY] Unresolved actor reference: ${dotPath} -> ${uuid}`);
        unresolvedRefs.set(dotPath, uuid);
        return UNRESOLVED_SENTINEL;
      }

      case 'vector2': {
        const raw = reader.readVector2();
        if (currentValue !== null && currentValue !== undefined &&
          typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
          (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y]);
          return currentValue;
        }
        return raw;
      }

      case 'vector3': {
        const raw = reader.readVector3();
        if (currentValue !== null && currentValue !== undefined &&
          typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
          (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z]);
          return currentValue;
        }
        return raw;
      }

      case 'euler': {
        const raw = reader.readEuler();
        if (currentValue !== null && currentValue !== undefined &&
          typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
          (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z]);
          return currentValue;
        }
        return raw;
      }

      case 'vector4':
      case 'quaternion': {
        const raw = reader.readVector4();
        if (currentValue !== null && currentValue !== undefined &&
          typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
          (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z, raw.w]);
          return currentValue;
        }
        return raw;
      }

      case 'array': {
        const firstWord = reader.readUint16();
        const existingArr = Array.isArray(currentValue) ? currentValue : [];

        if (firstWord === DELTA_ARRAY_SENTINEL) {
          // Delta mode: length unchanged, per-element bitmask follows.
          const count = existingArr.length;
          const present = reader.readBitmask(count);
          const result = [...existingArr]; // start from current state
          for (let i = 0; i < count; i++) {
            if (!present[i]) continue;
            if (net.element) {
              const elem = this.decodeValueFromBinary(
                reader, existingArr[i] ?? null, net.element,
                resolveActorRef, `${dotPath}.[${i}]`, unresolvedRefs
              );
              result[i] = elem === UNRESOLVED_SENTINEL ? null : elem;
            }
          }
          return result;
        }

        // Full mode: firstWord is the element count.
        const result: unknown[] = [];
        for (let i = 0; i < firstWord; i++) {
          if (net.element) {
            const elem = this.decodeValueFromBinary(
              reader, existingArr[i] ?? null, net.element,
              resolveActorRef, `${dotPath}.[${i}]`, unresolvedRefs
            );
            result.push(elem === UNRESOLVED_SENTINEL ? null : elem);
          }
        }
        return result;
      }

      case 'struct': {
        let instance = currentValue;
        if ((instance === null || instance === undefined) && net.structClass) {
          instance = new (net.structClass as new () => object)();
        }
        if (instance === null || instance === undefined) {
          throw new Error(
            `[ReplicationSerializer] Cannot decode struct for property "${dotPath}": ` +
            'no current instance and no structClass constructor in net metadata.'
          );
        }
        this.decodeObjectFromBinary(
          reader, instance as object, resolveActorRef, dotPath, unresolvedRefs
        );
        return instance;
      }

      case 'class': {
        let ctor: (new (...args: any[]) => any) | null = null;
        if (net.polymorphic) {
          ctor = readClassDiscriminator(reader);
          if (!ctor) {
            throw new Error(
              `[ReplicationSerializer] Unknown polymorphic class hash for property "${dotPath}". ` +
              'Packet is corrupt or the class was removed from ClassRegistry.'
            );
          }
        } else if (net.netBaseClass) {
          try {
            ctor = ClassRegistry.getClass(net.netBaseClass);
          } catch {
            throw new Error(
              `[ReplicationSerializer] Class "${net.netBaseClass}" not found in ClassRegistry for property "${dotPath}". ` +
              'Ensure the class is registered before receiving replication data.'
            );
          }
        }

        let instance = currentValue;
        if (ctor && (instance === null || instance === undefined || !(instance instanceof ctor))) {
          instance = new ctor();
        }

        if (instance === null || instance === undefined) {
          throw new Error(
            `[ReplicationSerializer] Cannot decode class for property "${dotPath}": ` +
            'no constructor resolved. Cannot continue decoding this packet.'
          );
        }
        this.decodeObjectFromBinary(
          reader, instance as object, resolveActorRef, dotPath, unresolvedRefs
        );
        return instance;
      }

      default:
        console.warn(`[ReplicationSerializer] Unknown netType in decode: ${(net as NetPropertyMetadata).netType}`);
        return currentValue;
    }
  }

  // ---------------------------------------------------------------------------
  // Shared helpers
  // ---------------------------------------------------------------------------

  private static fireRepCallback(
    obj: object,
    metadata: ClassReplicationMetadata,
    propName: string
  ): void {
    const callbackName = metadata.repCallbacks.get(propName);
    if (callbackName && typeof (obj as Record<string, unknown>)[callbackName] === 'function') {
      (obj as Record<string, () => void>)[callbackName]();
    }
  }

  private static withoutKeys(
    obj: Record<string, unknown>,
    keys: string[]
  ): Record<string, unknown> {
    const copy = { ...obj };
    for (const key of keys) delete copy[key];
    return copy;
  }
}

// Sentinel returned from decodeValueFromBinary for unresolved actor references.
// Callers check for this value and must NOT write it to the target object.
const UNRESOLVED_SENTINEL: unique symbol = Symbol('UNRESOLVED_ACTOR_REF');
type UnresolvedSentinel = typeof UNRESOLVED_SENTINEL;
// Suppress unused-type warning
type _UnresolvedSentinelUsed = UnresolvedSentinel;
